import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';

// Reuses the Angular formatTimestamp filter for exact parity with the rest of the app.
const formatTimestamp = value => {
  if (!value) return '-';
  const injector = angular.element('.ng-scope').injector();
  return injector.get('formatTimestampFilter')(value);
};

const getColumns = ({ onMarkHandled, pendingAlarmIds }) => [
  { accessorKey: 'id', header: 'ID', size: 80 },
  {
    accessorKey: 'name',
    header: 'Name',
    size: 180,
    Cell: ({ row }) => <span data-testid={`alarm-event-name-${row.original.id}`}>{row.original.name}</span>,
  },
  { accessorKey: 'ruleId', header: 'Rule ID', size: 90 },
  {
    id: 'ruleType',
    header: 'Rule Type',
    size: 150,
    accessorFn: alarm => alarm.ruleType || '',
    sortingFn: 'text',
    sortDescFirst: false,
    Cell: ({ row }) => <span data-testid={`alarm-event-type-${row.original.id}`}>{row.original.ruleType || '-'}</span>,
  },
  { accessorKey: 'occurrenceTotalCount', header: 'Occurrence Total Count', size: 120 },
  { accessorKey: 'triggeredCount', header: 'Triggered Count', size: 110 },
  {
    id: 'createdTs',
    header: 'Created',
    size: 130,
    accessorFn: alarm => {
      const timestamp = DateTime.fromISO(alarm.createdTs || '', { zone: 'utc' }).toMillis();
      return Number.isFinite(timestamp) ? timestamp : undefined;
    },
    sortingFn: 'basic',
    sortDescFirst: true,
    Cell: ({ row }) => <span data-testid={`alarm-event-created-${row.original.id}`}>{formatTimestamp(row.original.createdTs)}</span>,
  },
  {
    id: 'updatedTs',
    header: 'Updated',
    size: 130,
    accessorFn: alarm => formatTimestamp(alarm.updatedTs),
  },
  {
    id: 'isHandled',
    header: 'Acknowledged/Handled',
    size: 160,
    accessorFn: alarm => (alarm.isHandled ? 'Yes' : 'No'),
    Cell: ({ row }) => (
      <span className={`alarm-event-table__status ${row.original.isHandled ? 'is-handled' : 'is-unhandled'}`} data-testid={`alarm-event-handled-${row.original.id}`}>
        {row.original.isHandled ? 'Yes' : 'No'}
      </span>
    ),
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 90,
    enableSorting: false,
    enableHiding: false,
    Cell: ({ row }) => {
      const isPending = pendingAlarmIds.has(row.original.id);
      const idleLabel = row.original.isHandled ? 'Already handled' : 'Mark handled';
      const label = isPending ? 'Marking handled' : idleLabel;

      return (
        <span className="alarm-rule-table__actions">
          <Tooltip title={label}>
            <span>
              <IconButton
                size="small"
                disabled={row.original.isHandled || isPending}
                onClick={event => {
                  event.stopPropagation();
                  onMarkHandled(row.original);
                }}
                data-testid={`alarm-event-mark-handled-button-${row.original.id}`}
                aria-label={label}
                aria-busy={isPending}
              >
                {isPending ? (
                  <CircularProgress
                    size={20}
                    aria-label="Marking alarm handled"
                    data-testid={`alarm-event-mark-handled-progress-${row.original.id}`}
                  />
                ) : (
                  <CheckCircleOutlineIcon fontSize="small" color={row.original.isHandled ? 'disabled' : 'primary'} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </span>
      );
    },
  },
];

export const AlarmEventTable = ({
  alarms = [],
  isActive = false,
  loadBusy = false,
  showArchived = false,
  onShowArchivedChange,
  showHandled = true,
  onShowHandledChange,
  showUnhandled = true,
  onShowUnhandledChange,
  onMarkHandled,
  onView,
}) => {
  const pendingAlarmIdsRef = useRef(new Set());
  const [pendingAlarmIds, setPendingAlarmIds] = useState(() => new Set());
  const [sorting, setSorting] = useState([{ id: 'createdTs', desc: true }]);

  useEffect(() => {
    if (isActive) setSorting([{ id: 'createdTs', desc: true }]);
  }, [isActive]);

  const handleMarkHandled = async alarm => {
    if (alarm.isHandled || pendingAlarmIdsRef.current.has(alarm.id)) return;

    pendingAlarmIdsRef.current.add(alarm.id);
    setPendingAlarmIds(new Set(pendingAlarmIdsRef.current));
    try {
      await onMarkHandled(alarm);
    } finally {
      pendingAlarmIdsRef.current.delete(alarm.id);
      setPendingAlarmIds(new Set(pendingAlarmIdsRef.current));
    }
  };

  const visibleAlarms = alarms.filter(alarm => (alarm.isHandled ? showHandled : showUnhandled));

  return (
    <div className="alarm-event-table__wrapper" data-testid="alarm-event-table">
      <MaterialDataTable
        className="event-history-container consist-event-history"
        scrollContainerClassName="consist-event-history__scroll"
        tableTestId="alarm-event-list-table"
        fitViewport
        ariaLabel="Alarm event list"
        columns={getColumns({ onMarkHandled: handleMarkHandled, pendingAlarmIds })}
        data={visibleAlarms}
        isLoading={loadBusy}
        enableSorting
        sorting={sorting}
        onSortingChange={setSorting}
        enableTopToolbar
        enableHiding
        showHideColumnsTestId="alarm-event-show-hide-columns-button"
        muiTableBodyCellProps={({ row, column }) => ({
          'data-testid': `alarm-event-cell-${row.original.id}-${column.id}`,
        })}
        topToolbarActions={(
          <div className="alarm-event-table__filters">
            <label>
              <input
                type="checkbox"
                data-testid="alarm-event-show-archived-checkbox"
                checked={showArchived}
                onChange={event => onShowArchivedChange?.(event.target.checked)}
              />
              {'Show archived'}
            </label>
            <label>
              <input
                type="checkbox"
                data-testid="alarm-event-show-handled-checkbox"
                checked={showHandled}
                onChange={event => onShowHandledChange?.(event.target.checked)}
              />
              {'Handled'}
            </label>
            <label>
              <input
                type="checkbox"
                data-testid="alarm-event-show-unhandled-checkbox"
                checked={showUnhandled}
                onChange={event => onShowUnhandledChange?.(event.target.checked)}
              />
              {'Unhandled'}
            </label>
          </div>
        )}
        muiTableBodyRowProps={({ row }) => ({
          'data-testid': `alarm-event-row-${row.original.id}`,
          className: (row.original.archivedTs || row.original.isArchived)
            ? 'alarm-event-table__row--archived alarm-event-table__row--clickable'
            : 'alarm-event-table__row--clickable',
          onClick: () => onView(row.original),
        })}
      />
    </div>
  );
};

AlarmEventTable.propTypes = {
  alarms: PropTypes.array,
  isActive: PropTypes.bool,
  loadBusy: PropTypes.bool,
  showArchived: PropTypes.bool,
  onShowArchivedChange: PropTypes.func,
  showHandled: PropTypes.bool,
  onShowHandledChange: PropTypes.func,
  showUnhandled: PropTypes.bool,
  onShowUnhandledChange: PropTypes.func,
  onMarkHandled: PropTypes.func,
  onView: PropTypes.func,
};
